import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { adminApi } from '@/api/admin'

// Admin token storage keys (separate from user tokens)
const ADMIN_TOKEN_KEY = 'admin_token'
const ADMIN_TOKEN_EXPIRY_KEY = 'admin_token_expiry'
const ADMIN_USER_KEY = 'admin_user'

// Token expiry time (24 hours)
const TOKEN_EXPIRY = 24 * 60 * 60 * 1000

export interface AdminUser {
  id: number
  email: string
  username?: string
  display_name?: string
  is_super_admin?: boolean
}

export const useAdminStore = defineStore('admin', () => {
  // State
  const token = ref<string>(localStorage.getItem(ADMIN_TOKEN_KEY) || '')
  const user = ref<AdminUser | null>(null)
  
  // Load user from localStorage on init
  const savedUser = localStorage.getItem(ADMIN_USER_KEY)
  if (savedUser) {
    try {
      user.value = JSON.parse(savedUser)
    } catch {
      user.value = null
    }
  }
  
  // Getters
  const isLoggedIn = computed(() => {
    if (!token.value) return false
    const expiry = localStorage.getItem(ADMIN_TOKEN_EXPIRY_KEY)
    if (expiry && Date.now() > parseInt(expiry)) {
      logout()
      return false
    }
    return true
  })
  
  const displayName = computed(() => {
    if (!user.value) return 'Admin'
    return user.value.username || user.value.display_name || user.value.email?.split('@')[0] || 'Admin'
  })
  
  // Actions
  async function login(email: string, password: string) {
    // Direct admin login via dedicated admin API
    const response = await adminApi.login(email, password)
    const { user: userData, token: tokenData } = response.data.data
    
    // Store admin info
    user.value = {
      id: userData.id,
      email: userData.email,
      username: userData.username,
      display_name: userData.display_name,
      is_super_admin: userData.is_super_admin === true
    }
    token.value = tokenData
    
    localStorage.setItem(ADMIN_TOKEN_KEY, tokenData)
    localStorage.setItem(ADMIN_TOKEN_EXPIRY_KEY, String(Date.now() + TOKEN_EXPIRY))
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user.value))
    
    return true
  }
  
  function logout() {
    token.value = ''
    user.value = null
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    localStorage.removeItem(ADMIN_TOKEN_EXPIRY_KEY)
    localStorage.removeItem(ADMIN_USER_KEY)
  }
  
  function checkAuth() {
    if (!isLoggedIn.value) {
      logout()
      return false
    }
    return true
  }
  
  return {
    token,
    user,
    isLoggedIn,
    displayName,
    login,
    logout,
    checkAuth
  }
})